import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Sun, SignOut } from "@phosphor-icons/react";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/constants";
import { NotificationBell } from "@/components/Notifications/NotificationBell";
import { AISearch } from "@/components/common/AISearch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import api from "@/lib/api";
import { toast } from "sonner";

export const Header = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [quickJump, setQuickJump] = useState("");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleQuickJump = async (e) => {
    e.preventDefault();
    if (!quickJump.trim()) return;
    try {
      const { data } = await api.get("/projects", { params: { search: quickJump.trim() } });
      if (data.length > 0) {
        navigate(`/projects/${data[0].id}`);
        setQuickJump("");
      } else {
        toast.error(`No project found matching "${quickJump}"`);
      }
    } catch {
      toast.error("Quick jump failed");
    }
  };

  return (
    <header className="h-16 flex items-center px-3 sm:px-6 gap-2 sm:gap-4 sticky top-0 z-20" style={{ background: "hsl(var(--header-bg))" }} data-testid="header">
      <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
        <div className="w-8 h-8 flex items-center justify-center shrink-0" data-testid="header-logo-badge" style={{
          borderRadius: "7px",
          background: "linear-gradient(145deg, #7dd8fb 0%, #38bdf8 45%, #0369a1 100%)",
          boxShadow: "inset 0 1px 1px rgba(255,255,255,0.55), inset 0 -1px 2px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.35)",
        }}>
          <span className="font-display font-black text-sm text-white">PH</span>
        </div>
        <span className="font-display font-extrabold text-sm sm:text-base whitespace-nowrap hidden sm:inline-block" style={{ color: "hsl(var(--header-fg))" }}>Project Hub</span>
        <span className="w-px h-4 hidden sm:block" style={{ background: "hsl(var(--header-fg) / 0.25)" }} />
        <span className="text-sm font-semibold hidden sm:block" style={{ color: "hsl(var(--header-accent))" }} data-testid="header-enterprise-badge">Enterprise</span>
        <span className="text-xs font-bold tracking-widest uppercase hidden lg:block" style={{ color: "hsl(var(--header-fg) / 0.7)" }} data-testid="header-fastpath-badge">Fast Path</span>
      </div>

      <form onSubmit={handleQuickJump} className="relative shrink-0 hidden md:block">
        <input
          data-testid="quick-jump-input"
          value={quickJump}
          onChange={(e) => setQuickJump(e.target.value)}
          placeholder="P72107…"
          className="w-32 h-9 px-3 text-base focus:outline-none focus:ring-1 focus:ring-[hsl(var(--header-accent))] transition-colors"
          style={{ borderRadius: "2px", background: "hsl(var(--header-fg) / 0.1)", color: "hsl(var(--header-fg))", border: "1px solid hsl(var(--header-fg) / 0.2)" }}
        />
      </form>

      <div className="flex-1 min-w-[70px] max-w-xl">
        <AISearch />
      </div>
      <div className="flex items-center gap-1 sm:gap-3 ml-auto pl-1 sm:pl-6">
        <button
          data-testid="theme-toggle-btn"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          className="p-1.5 sm:p-2 transition-colors hover:bg-[hsl(var(--header-fg)/0.1)]"
          style={{ borderRadius: "2px", border: "1px solid hsl(var(--header-fg) / 0.2)", color: "hsl(var(--header-fg))" }}
        >
          {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
        </button>
        <NotificationBell />
        <div className="flex items-center gap-1.5 sm:gap-2 pl-1.5 sm:pl-3" style={{ borderLeft: "1px solid hsl(var(--header-fg) / 0.2)" }}>
          <Avatar className="h-8 w-8 shrink-0" style={{ borderRadius: "2px" }}>
            <AvatarFallback className="text-sm font-semibold" style={{ borderRadius: "2px", background: "hsl(var(--header-accent))", color: "hsl(var(--header-bg))" }}>
              {user?.name?.slice(0, 2)?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:block">
            <div className="text-base font-semibold leading-none" style={{ color: "hsl(var(--header-fg))" }} data-testid="header-user-name">{user?.name}</div>
            <div className="text-sm" style={{ color: "hsl(var(--header-fg) / 0.6)" }}>{ROLE_LABELS[user?.role]}{user?.department ? ` · ${user.department}` : ""}</div>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout} className="p-1.5 sm:p-2 transition-colors hover:bg-[hsl(var(--header-fg)/0.1)] shrink-0" style={{ borderRadius: "2px", color: "hsl(var(--header-fg))" }}>
            <SignOut size={17} />
          </button>
        </div>
      </div>
    </header>
  );
};
